import React, { useState, useEffect, useContext, useRef } from "react";
import api from "../context/api";
import { AuthContext } from "../context/AuthContext";
import styled, { keyframes } from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MtnLogo from "../Images/MTN.jpeg";
import VodafoneLogo from "../Images/Telecel-Cash-Logo.jpg";
import AirtelTigoLogo from "../Images/airteltigo_.jpg";

const paymentMethods = [
  { name: "MTN", logo: MtnLogo, provider: "MTN", channel: "mtn" },
  { name: "VOD", logo: VodafoneLogo, provider: "Telecel", channel: "vod" },
  { name: "ATL", logo: AirtelTigoLogo, provider: "AirtelTigo", channel: "atl" },
];

const Deposit = () => {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpVisible, setOtpVisible] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpReference, setOtpReference] = useState(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  // userBalance is kept live by the socket listener in AuthContext, and the
  // pending deposit reference is polled app-wide there too, so it keeps
  // getting verified (and credited) even after leaving this page.
  const {
    userBalance,
    fetchUserData,
    pendingDepositReference,
    registerPendingDeposit,
    clearPendingDeposit,
  } = useContext(AuthContext);

  useEffect(() => {
    const fetchPhoneNumber = async () => {
      try {
        const response = await api.get("/user/phoneNumber");
        setPhoneNumber(response.data.phoneNumber);
      } catch (error) {
        console.error("Error fetching phone number:", error);
      }
    };

    fetchPhoneNumber();
  }, []);

  // Shows a one-time toast when the app-wide poll (in AuthContext) resolves
  // a deposit while this page happens to be mounted.
  const previousPendingReferenceRef = useRef(null);

  useEffect(() => {
    if (previousPendingReferenceRef.current && !pendingDepositReference) {
      toast.success("Deposit confirmed successfully.");
    }
    previousPendingReferenceRef.current = pendingDepositReference;
  }, [pendingDepositReference]);

  const formatNumberWithCommas = (number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    setDropdownVisible(false);
  };

  const handleOtpSubmit = async () => {
    if (!otpCode.trim()) {
      toast.error("Please enter the code sent to your phone.");
      return;
    }

    if (!otpReference) {
      toast.error("We could not find the payment reference for this OTP. Please start the deposit again.");
      return;
    }

    try {
      setOtpSubmitting(true);
      const response = await api.post("/deposit/submit-otp", {
        otp: otpCode.trim(),
        reference: otpReference,
      });

      const data = response.data;

      if (!data.success) {
        const msg = data.message || "We couldn't verify that code. Please confirm the OTP and try again.";
        toast.error(msg);
        return;
      }

      const nextStatus = String(data.data?.status || "").toLowerCase();
      const verificationReference = data.data?.reference || otpReference;

      if (nextStatus === "success") {
        clearPendingDeposit();
        fetchUserData?.();
        toast.success(data.message || "Deposit confirmed successfully.");
      } else if (verificationReference) {
        registerPendingDeposit(verificationReference);
        toast.info(
          data.message ||
            "Deposit authorization received. Approve any phone prompt and we will keep checking automatically."
        );
      }

      setOtpVisible(false);
      setOtpCode("");
      setOtpReference(null);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Something went wrong while submitting the OTP.";
      toast.error(msg);
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (parseFloat(amount) < 1) {
      setLoading(false);
      toast.error("Amount less than 1 cedis not accepted");
      return;
    }

    if (!paymentMethod) {
      setLoading(false);
      toast.error("Please choose a mobile money network to continue.");
      return;
    }

    const requestData = {
      amount: parseFloat(amount),
      customerMsisdn: phoneNumber,
      description: "Deposit to Huntex",
      channel: paymentMethod.channel,
    };

    try {
      const paymentResponse = await api.post("/deposit/initiate", requestData);
      const responseData = paymentResponse.data;

      if (!responseData.success) {
        const msg = responseData.message || "We couldn't start the payment. Please check your number and try again.";
        toast.error(msg);
        return;
      }

      const status = responseData.data?.status;
      const reference = responseData.data?.reference;
      const displayText = responseData.data?.display_text || responseData.data?.displayText;

      if (status === "send_otp" && reference) {
        setOtpReference(reference);
        setOtpVisible(true);
        toast.info(
          displayText ||
            "Paystack requested an OTP for this charge. Enter the code sent to your phone to continue."
        );
      } else if (status === "pay_offline" && reference) {
        registerPendingDeposit(reference);
        toast.info(displayText || "Approve the payment prompt on your phone. We will confirm it automatically.");
      } else {
        toast.info(displayText || "Please approve the payment prompt on your phone.");
      }
    } catch (error) {
      if (!error.response) {
        toast.error("Please check your internet connection and try again.");
      } else {
        const serverMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.message ||
          "Something went wrong while starting the payment.";
        toast.error(serverMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <Header>Deposit to Your Account</Header>
      <FormCard>
        <Card>
          <CardTitle>Current Balance</CardTitle>
          <CardContent>GH₵ {userBalance ? formatNumberWithCommas(userBalance.toFixed(2)) : "0.00"}</CardContent>
        </Card>
        <InputLabel>Mobile Number</InputLabel>
        <Input type="text" placeholder="Enter your mobile number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
        <InputLabel>Amount (GH₵)</InputLabel>
        <Input type="number" placeholder="Enter amount to deposit" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <InputLabel>Payment Method</InputLabel>
      <DropdownWrapper>
        <DropdownButton onClick={() => setDropdownVisible(!dropdownVisible)}>
          {paymentMethod ? (
            <>
              <img src={paymentMethod.logo} alt={paymentMethod.name} width={40} />
              <span>{paymentMethod.provider}</span>
            </>
          ) : (
            "Select Payment Method"
          )}
        </DropdownButton>
        {dropdownVisible && (
          <DropdownMenu>
            {paymentMethods.map((method, index) => (
              <DropdownItem key={index} onClick={() => handlePaymentMethodSelect(method)}>
                <img src={method.logo} alt={method.name} width={40} />
                {method.provider}
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </DropdownWrapper>
      <SubmitButton onClick={handleSubmit} disabled={loading}>{loading ? "Processing..." : "Top Up Now"}</SubmitButton>
      {otpVisible && (
        <OtpModal>
          <OtpModalContent>
            <OtpCloseButton onClick={() => setOtpVisible(false)} disabled={otpSubmitting}>×</OtpCloseButton>
            <OtpLabel>Enter code sent to your phone number</OtpLabel>
            <OtpInput
              type="text"
              placeholder="6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              maxLength={6}
            />
            <OtpButton onClick={handleOtpSubmit} disabled={otpSubmitting}>
              {otpSubmitting ? "Confirming..." : "Confirm"}
            </OtpButton>
          </OtpModalContent>
        </OtpModal>
      )}
      </FormCard>
    </Container>
  );
};



const slideDown = keyframes`
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #134b5f 0%, #1a5f73 100%);
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.h1`
  color: #ffffff;
  margin-bottom: 40px;
  font-size: 32px;
  font-weight: bold;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;
const FormCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  animation: ${slideDown} 0.5s ease-in-out;
`;




const Card = styled.div`
  background: linear-gradient(135deg, #03750b, #025a08);
  padding: 24px;
  text-align: center;
  margin-bottom: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    pointer-events: none;
  }
`;

const CardTitle = styled.h2`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const CardContent = styled.p`
  font-size: 36px;
  font-weight: bold;
  color: #ffffff;
  position: relative;
  z-index: 1;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  margin-top: 20px;
`;

const Input = styled.input`
  width: 90%;
  padding: 14px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:focus {
    border-color: #03750b;
    outline: none;
    background-color: #ffffff;
    box-shadow: 0 4px 8px rgba(3, 117, 11, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const DropdownWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  background: #fafafa;
  color:#333;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: #03750b;
    background: #ffffff;
    box-shadow: 0 4px 8px rgba(3, 117, 11, 0.1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  overflow: hidden;
  animation: ${slideDown} 0.3s ease-in-out;
`;

const DropdownItem = styled.div`
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fdf9;
    padding-left: 20px;
  }
  
  img {
    border-radius: 4px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #03750b, #025a08);
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 32px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(3, 117, 11, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover::before {
    width: 300px;
    height: 300px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(3, 117, 11, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    box-shadow: none;
    
    &:hover {
      transform: none;
    }
  }
`;

const OtpModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const OtpModalContent = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const OtpCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(253, 7, 7, 1);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(200, 7, 7, 1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OtpLabel = styled.p`
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
`;

const OtpHint = styled.p`
  font-size: 12px;
  margin-bottom: 12px;
  color: #666;
  line-height: 1.4;
`;

const OtpInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 2px solid #ccc;
  border-radius: 5px;
  font-size: 16px;
  margin-bottom: 15px;

  &:focus {
    border-color: #134b5f;
    outline: none;
  }
`;

const OtpButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #134b5f;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background-color: #0d3a4a;
  }

  &:disabled {
    background-color: gray;
    cursor: not-allowed;
  }
`;


export default Deposit;